import React, { useState, useEffect } from 'react';
import { useParams, NavLink } from 'react-router-dom';
import Latex from 'react-latex-next';
import 'katex/dist/katex.min.css';
import { loadVolume } from '../data/volumes.js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope } from '@fortawesome/free-solid-svg-icons';
import coverImage from "../images/cover-image-original.jpg";
import { FaDownload } from "react-icons/fa";
import viewPDF from "../images/viewPDF.png";
import orcidIcon from "../images/orcid.png";
import CiteButton from "./CiteButton.js";


const ViewArticlePageContent = () => {

    const { year, id } = useParams();

    const [article, setArticle] = useState(null);
    // The rest of the volume, kept only so the Cite dialog can tell two papers
    // by the same author in the same year apart (Surname_Year -> …a, …b).
    const [volume, setVolume] = useState([]);
    const [status, setStatus] = useState('loading'); // loading | ready | missing | error
    const [selectedAuthor, setSelectedAuthor] = useState(null);
    const [viewArticle, setViewArticle] = useState(false);

    // Only this article's volume is fetched, and only once — the same cached
    // promise backs the volume listing the reader most likely arrived from.
    useEffect(() => {
        let active = true;
        setStatus('loading');
        setSelectedAuthor(null);
        setViewArticle(false);

        loadVolume(year)
            .then((articles) => {
                if (!active) return;
                const found = articles.find((entry) => entry.documentName === id) || null;
                setVolume(articles);
                setArticle(found);
                setStatus(found ? 'ready' : 'missing');
            })
            .catch(() => {
                if (!active) return;
                setVolume([]);
                setArticle(null);
                setStatus('error');
            });

        return () => {
            active = false;
        };
    }, [year, id]);

    const handleAuthorClick = (author) => {
        setSelectedAuthor(author === selectedAuthor ? null : author);
    };

    const handleViewArticle = () => {
        setViewArticle(!viewArticle);
    }


    if (status === 'loading') {
        return (
            <section className="main-section">
                <div className="container">
                    <div className="volume-status" role="status" aria-live="polite">
                        <div className="loader"></div>
                    </div>
                </div>
            </section>
        );
    }

    if (status !== 'ready' || !article) {
        return (
            <section className="main-section">
                <div className="container">
                    <div className="volume-status">
                        <p>
                            {status === 'error'
                                ? 'This article could not be loaded. Please try again.'
                                : 'Article not found.'}
                        </p>
                        <p>
                            <NavLink to={`/volumes/${year}`}>Back to volume {year}</NavLink>
                        </p>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <div> 
            <section className="main-section">
                <div className="container">
                    <div className="header">
                        <div className="logo">
                            <img src={coverImage} alt="Logo" />
                            <p>Volume: {year}</p>
                        </div>  
                    </div>
                    <div className="article-detail">
                        <h1><Latex>{article.title}</Latex></h1>
                        <p><strong>Authors:</strong> {article.authors.map((author, index) => (
                            <span key={index}>
                            <span className="author" onClick={() => handleAuthorClick(author)}>
                                {author.name} {selectedAuthor === author ? '-' : '+'}
                                {author.orcid && (
                                    <a
                                    href={author.orcid}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ display: 'inline-flex', alignItems: 'center', marginLeft: "0.2em" }}
                                    >
                                    <img
                                        src={orcidIcon}
                                        alt="ORCID profile"
                                        style={{ height: '0.8em', width: '0.8em' }}
                                    />
                                    </a>
                                )}
                            </span>
                            {index < article.authors.length - 1 && ', '}
                            </span>
                        ))}</p>
                        {selectedAuthor && (
                            <div className='author-detail'>
                                {selectedAuthor.address.map((address, index) => (
                                    <p key={index}>{address}</p>
                                ))}
                                <div>
                                    <FontAwesomeIcon icon={faEnvelope}/>
                                    <a href={() => false}> </a>
                                    <a className="author-mail" href={`mailto:${selectedAuthor.email}`}>{selectedAuthor.email}</a>
                                </div>
                            </div>
                        )}
                        <p><strong>Published Online:</strong> {article.publishedOnline}</p>
                        <p><strong>Page Range:</strong> {article.pageRange}</p>
                        
                        <p><strong>Abstract: </strong><Latex>{article.abstract}</Latex></p>
                        <p/>
                        <p><strong>Keywords: </strong>{article.keywords.join(', ')}</p>
                        <p><strong>{article.classification.year}: </strong>{article.classification.types.join(', ')}</p>
                        <p><string>DOI: </string><a href={`https://${article.doi}`}>{article.doi}</a></p>
                        <p className="document">
                            <div className="pdf">
                                <button className="img-btn" onClick={handleViewArticle}>
                                    <img className="img" src={viewPDF} alt="View" />
                                </button>
                                <a href={article.path} download={article.documentName} target='_blank' rel="noreferrer">
                                    <FaDownload className="icon"/>
                                </a>
                                <CiteButton article={article} siblings={volume} year={year} variant="icon" />
                            </div>
                        </p>
                        { viewArticle ? <embed src={article.path} width="100%" height="1000px" /> : <div/> }
                    </div>
                </div>
            </section>
        </div>
    );
};

export default ViewArticlePageContent;